import { z } from 'zod/v4';
import { RuleTemplateRuleSchema } from './rule-template.js';

/**
 * `GET /risk-analysis/assessments/:id/rules/matched` params, ported from
 * `2025-06/risk-analysis-service/routes/http/v3/assessments/matched.js`, which
 * reused `assessment.listMatchedRules.params`.
 */
export const GetMatchedRulesParamsSchema = z.object({
  id: z.string().min(1).max(36),
});

export type GetMatchedRulesParams = z.infer<typeof GetMatchedRulesParamsSchema>;

/**
 * `GET /risk-analysis/assessments/:id/rules/matched` querystring, ported from
 * `assessment.listMatchedRules.querystring`
 * (`2025-06/risk-analysis-service/routes/schemas/v3/request/assessment.js`).
 *
 * The legacy schema also spread `...paginationParams` (`page`/`count`), but the
 * handler never read them — dropped here as accepted-but-ignored noise.
 */
export const GetMatchedRulesQuerySchema = z.object({
  triggerEvent: z.string().min(1),
  /** Comma-separated asset-type keys, e.g. `AUDITS,CERTIFICATES`. */
  assets: z.string().min(1),
});

export type GetMatchedRulesQuery = z.infer<typeof GetMatchedRulesQuerySchema>;

/**
 * One mitigation recorded against a matched rule's trigger.
 *
 * Legacy embedded the full `buildMitigationDatapoints` + `getAsset` output as
 * `assetDetail` (mitigation id, metric id, value, remark, asset type/id, a
 * resolved asset name, ...). Neither frontend reads anything off it besides
 * `mitigationAssetValue` (see `mitigationDocumentTarget.ts` in frontend-v2, which
 * derives the document kind from the trigger's own `dataPoint` label instead) —
 * trimmed to that one field rather than porting the unused asset-name lookup.
 */
export const MatchedRuleMitigationSchema = z.object({
  triggerId: z.string(),
  assetDetail: z.object({
    mitigationAssetValue: z.string().nullable(),
  }),
});

export type MatchedRuleMitigation = z.infer<typeof MatchedRuleMitigationSchema>;

/**
 * One rule from the template, annotated with whether it's satisfied for this
 * assessment. `ruleDetails` is the same transformed-rule shape
 * `GET /risk-analysis/rule-templates/:id` returns per rule.
 */
export const MatchedRuleSchema = z.object({
  rulePassed: z.boolean(),
  ruleDetails: RuleTemplateRuleSchema,
  ruleMitigations: z.array(MatchedRuleMitigationSchema),
});

export type MatchedRule = z.infer<typeof MatchedRuleSchema>;

/**
 * `GET /risk-analysis/assessments/:id/rules/matched` response, ported from
 * `listMatchedMitigationDatapoints`
 * (`2025-06/risk-analysis-service/handlers/v3/assessments/list-datapoints/matched-datapoints.js`).
 */
export const MatchedRulesResponseSchema = z.object({
  id: z.string(),
  scoreFormula: z.string().nullable(),
  isActive: z.number(),
  rules: z.array(MatchedRuleSchema),
});

export type MatchedRulesResponse = z.infer<typeof MatchedRulesResponseSchema>;

/**
 * `GET /risk-analysis/assessments/:id/datapoints/:riskId` params, ported from
 * `2025-06/risk-analysis-service/routes/http/v3/assessments/list-datapoints.js`,
 * which reused `assessment.listDatapoints.params`.
 *
 * Response reuses `MatchedRulesResponseSchema` — same `{id, scoreFormula,
 * isActive, rules}` shape as `GET /rules/matched`, a sibling question over the
 * same rule template ("which rules produced this risk factor's score" vs
 * "which rules matched this trigger event").
 *
 * The legacy querystring also spread `...paginationParams` (`page`/`count`),
 * but the handler never read them — dropped here as accepted-but-ignored
 * noise, same as `GetMatchedRulesQuerySchema`'s history.
 */
export const GetRiskFulfilledRulesParamsSchema = z.object({
  id: z.string().min(1).max(36),
  riskId: z.string().min(1).max(36),
});

export type GetRiskFulfilledRulesParams = z.infer<
  typeof GetRiskFulfilledRulesParamsSchema
>;
